// src/features/calendar/settings/PoliciesSettings.tsx
// Admin: calendar policies — who may schedule what, and the defaults a scope
// (calendar / team / org) inherits. Includes lookup of a policy by entity.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarCalendarPolicy, useI18n } from '@react/hooks';
import { Button, Input, Textarea, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type { CalendarPolicy, EventVisibility } from '@core/types/calendar';
import { ModalShell } from '../ModalShell';
import { errMessage, makeEntity } from '../time';

const VISIBILITIES: EventVisibility[] = [
  'EVENT_VISIBILITY_DEFAULT', 'EVENT_VISIBILITY_PUBLIC', 'EVENT_VISIBILITY_PRIVATE', 'EVENT_VISIBILITY_CONFIDENTIAL',
];

export const PoliciesSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendarCalendarPolicy();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarPolicy | null>(null);
  const [deleting, setDeleting] = useState<CalendarPolicy | null>(null);

  // form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeType, setScopeType] = useState('calendar');
  const [scopeId, setScopeId] = useState('');
  const [defaultVisibility, setDefaultVisibility] = useState<EventVisibility>('EVENT_VISIBILITY_DEFAULT');
  const [maxDaysAhead, setMaxDaysAhead] = useState<number | ''>('');
  const [enableAttendees, setEnableAttendees] = useState(true);
  const [enableRecurrence, setEnableRecurrence] = useState(true);
  const [enableConference, setEnableConference] = useState(true);
  const [autoAddMeetLink, setAutoAddMeetLink] = useState(false);
  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // lookup by entity
  const [lookupType, setLookupType] = useState('calendar');
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<CalendarPolicy | null | 'none'>(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  const load = () => {
    setError(null);
    store.ListCalendarPolicies().catch((e: unknown) => setError(errMessage(e, t('calendar.policies.loadError'))));
  };

  useEffect(() => { load(); }, []);

  const openDialog = async (listed: CalendarPolicy | null) => {
    let p = listed;
    if (listed?.entity?.id) {
      // Load the full policy — the list payload may be trimmed.
      try {
        p = await store.GetCalendarPolicy(listed.entity.id);
      } catch {
        p = listed;
      }
    }
    setEditing(p);
    setName(p?.name ?? '');
    setDescription(p?.description ?? '');
    setScopeType(p?.scope_entity?.type_name ?? p?.scope_entity_type ?? 'calendar');
    setScopeId(p?.scope_entity?.id ?? '');
    setDefaultVisibility(p?.default_visibility ?? 'EVENT_VISIBILITY_DEFAULT');
    setMaxDaysAhead(p?.max_days_ahead ?? '');
    setEnableAttendees(p?.enable_attendees ?? true);
    setEnableRecurrence(p?.enable_recurrence ?? true);
    setEnableConference(p?.enable_conference ?? true);
    setAutoAddMeetLink(p?.auto_add_meet_link ?? false);
    setWorkingHoursEnabled(p?.working_hours_enabled ?? true);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editing) {
        await store.UpdateCalendarPolicy(editing.entity?.id ?? '', {
          policy: {
            ...editing,
            name: name.trim(),
            description,
            default_visibility: defaultVisibility,
            max_days_ahead: maxDaysAhead === '' ? undefined : maxDaysAhead,
            enable_attendees: enableAttendees,
            enable_recurrence: enableRecurrence,
            enable_conference: enableConference,
            auto_add_meet_link: autoAddMeetLink,
            working_hours_enabled: workingHoursEnabled,
          },
          field_mask: [
            'name', 'description', 'default_visibility', 'max_days_ahead', 'enable_attendees',
            'enable_recurrence', 'enable_conference', 'auto_add_meet_link', 'working_hours_enabled',
          ],
        });
      } else {
        await store.CreateCalendarPolicy({
          entity: makeEntity('', 'calendar_policy', scope),
          name: name.trim(),
          description: description || undefined,
          scope_entity: makeEntity(scopeId.trim(), scopeType.trim() || 'calendar', scope),
          scope_entity_type: scopeType.trim() || undefined,
          default_visibility: defaultVisibility,
          max_days_ahead: maxDaysAhead === '' ? undefined : maxDaysAhead,
          enable_attendees: enableAttendees,
          enable_recurrence: enableRecurrence,
          enable_conference: enableConference,
          auto_add_meet_link: autoAddMeetLink,
          working_hours_enabled: workingHoursEnabled,
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('calendar.policies.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const lookup = async () => {
    if (!lookupId.trim()) return;
    setLookupBusy(true);
    setLookupResult(null);
    try {
      const p = await store.GetCalendarPolicyByEntity(lookupType.trim() || 'calendar', lookupId.trim());
      setLookupResult(p ?? 'none');
    } catch {
      setLookupResult('none');
    } finally {
      setLookupBusy(false);
    }
  };

  const flag = (label: string, value: boolean, set: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('calendar.policies.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>
          {t('calendar.policies.newPolicy')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {store.isLoading && store.items.length === 0 && (
        <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />
      )}

      {!store.isLoading && store.items.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('calendar.policies.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((p) => (
          <div key={p.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{p.name}</span>
                {p.scope_entity_type && <Badge variant="default">{p.scope_entity_type}</Badge>}
                {!p.is_active && <Badge variant="warning">{t('calendar.policies.inactive')}</Badge>}
              </div>
              {p.description && <p className="mt-0.5 truncate text-sm text-muted-foreground">{p.description}</p>}
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openDialog(p)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(p)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Lookup by entity */}
      <div className="space-y-2 rounded-md border border-border p-3">
        <span className="block text-sm font-medium text-foreground">{t('calendar.policies.lookupTitle')}</span>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            label={t('calendar.policies.entityType')}
            value={lookupType}
            onChange={(e) => setLookupType(e.target.value)}
            fullWidth={false}
            className="w-36"
          />
          <Input
            label={t('calendar.policies.entityId')}
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            fullWidth={false}
            className="w-72"
          />
          <Button size="sm" variant="secondary" onClick={lookup} isLoading={lookupBusy} disabled={!lookupId.trim()}>
            {t('calendar.policies.lookup')}
          </Button>
        </div>
        {lookupResult === 'none' && (
          <p className="text-sm text-muted-foreground">{t('calendar.policies.lookupNone')}</p>
        )}
        {lookupResult && lookupResult !== 'none' && (
          <p className="text-sm text-foreground">
            {t('calendar.policies.lookupFound', { name: lookupResult.name })}
          </p>
        )}
      </div>

      <ModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('calendar.policies.editPolicy') : t('calendar.policies.newPolicy')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={save} isLoading={isSaving} disabled={!name.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          {!editing && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                label={t('calendar.policies.entityType')}
                value={scopeType}
                onChange={(e) => setScopeType(e.target.value)}
                placeholder="calendar"
              />
              <Input
                label={t('calendar.policies.entityId')}
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
              />
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Select
              label={t('calendar.policies.defaultVisibility')}
              value={defaultVisibility}
              onChange={(e) => setDefaultVisibility(e.target.value as EventVisibility)}
              options={VISIBILITIES.map((v) => ({ value: v, label: t(`calendar.visibilityValue.${v.replace('EVENT_VISIBILITY_', '').toLowerCase()}`) }))}
            />
            <Input
              label={t('calendar.policies.maxDaysAhead')}
              type="text"
              inputMode="numeric"
              value={maxDaysAhead === '' ? '' : String(maxDaysAhead)}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                setMaxDaysAhead(v === '' ? '' : parseInt(v, 10));
              }}
            />
          </div>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {flag(t('calendar.eventTypes.enableAttendees'), enableAttendees, setEnableAttendees)}
            {flag(t('calendar.eventTypes.enableRecurrence'), enableRecurrence, setEnableRecurrence)}
            {flag(t('calendar.eventTypes.enableConference'), enableConference, setEnableConference)}
            {flag(t('calendar.policies.autoAddMeetLink'), autoAddMeetLink, setAutoAddMeetLink)}
            {flag(t('calendar.policies.workingHoursEnabled'), workingHoursEnabled, setWorkingHoursEnabled)}
          </div>
        </div>
      </ModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('calendar.policies.deleteTitle')}
        description={t('calendar.policies.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteCalendarPolicy(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('calendar.policies.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
